import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Loader2, Search, UserPlus, Check, X, AtSign,
  MessageCircle, UserMinus, Ban, Undo2,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import Avatar from '../../components/Avatar'
import { useFriends, useMyHandle } from '../../hooks/useFriends'
import { cn } from '../../lib/utils'

// /friends — the friendship graph surface. Set your @handle, find a
// friend by their exact handle, manage requests, and jump into the
// friend-scoped features (messages today; Wordle results and Spy
// invites in follow-up PRs). Couples features intentionally don't key
// off this page — they wait for a partner, not a friend.
export function FriendsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const friendsApi = useFriends()
  const { friends, incoming, outgoing, blocked, loading, error } = friendsApi

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('friends.title')}
        </h1>
      </div>

      <HandleCard t={t} />
      <SearchCard t={t} friendsApi={friendsApi} />

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      )}

      {!loading && (
        <>
          {friendsApi.suggestions.length > 0 && (
            <Section title={t('friends.suggestionsTitle')}>
              {friendsApi.suggestions.map((s) => (
                <SuggestionRow
                  key={s.user_id}
                  suggestion={s}
                  onAdd={() => friendsApi.sendRequest(s.user_id)}
                  t={t}
                />
              ))}
            </Section>
          )}

          {incoming.length > 0 && (
            <Section title={t('friends.incoming')}>
              {incoming.map((row) => (
                <FriendRow key={row.friendship_id} row={row}>
                  <Button size="sm" onClick={() => friendsApi.accept(row)}>
                    <Check className="w-4 h-4 me-1" />
                    {t('friends.accept')}
                  </Button>
                  <IconAction
                    icon={X}
                    label={t('friends.decline')}
                    onConfirm={() => friendsApi.remove(row)}
                    t={t}
                  />
                </FriendRow>
              ))}
            </Section>
          )}

          {outgoing.length > 0 && (
            <Section title={t('friends.outgoing')}>
              {outgoing.map((row) => (
                <FriendRow key={row.friendship_id} row={row}>
                  <IconAction
                    icon={X}
                    label={t('friends.cancel')}
                    onConfirm={() => friendsApi.remove(row)}
                    t={t}
                  />
                </FriendRow>
              ))}
            </Section>
          )}

          <Section title={t('friends.friendsList')}>
            {friends.length === 0 && (
              <p className="p-3 text-sm text-muted-foreground" dir="auto">{t('friends.empty')}</p>
            )}
            {friends.map((row) => (
              <FriendRow key={row.friendship_id} row={row}>
                <button
                  type="button"
                  onClick={() => navigate(`/messages/${row.other_user_id}`)}
                  aria-label={t('friends.message')}
                  className="p-2 rounded-md hover:bg-accent text-app-primary-500"
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
                <IconAction
                  icon={UserMinus}
                  label={t('friends.unfriend')}
                  onConfirm={() => friendsApi.remove(row)}
                  t={t}
                />
                <IconAction
                  icon={Ban}
                  label={t('friends.block')}
                  onConfirm={() => friendsApi.block(row)}
                  t={t}
                />
              </FriendRow>
            ))}
          </Section>

          {blocked.length > 0 && (
            <Section title={t('friends.blockedList')}>
              {blocked.map((row) => (
                <FriendRow key={row.friendship_id} row={row}>
                  <IconAction
                    icon={Undo2}
                    label={t('friends.unblock')}
                    onConfirm={() => friendsApi.remove(row)}
                    t={t}
                  />
                </FriendRow>
              ))}
            </Section>
          )}
        </>
      )}

      {error && <p className="text-xs text-red-500 px-1" dir="auto">{error}</p>}
    </div>
  )
}

// ─── Your @handle ───────────────────────────────────────────────────
function HandleCard({ t }) {
  const { identity, loading, save } = useMyHandle()
  const [editing, setEditing] = useState(false)
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [state, setState] = useState(null) // null | 'saving' | 'saved' | error key/message

  if (loading) return null

  const startEdit = () => {
    setUsername(identity.username)
    setDisplayName(identity.display_name)
    setState(null)
    setEditing(true)
  }

  const submit = async (e) => {
    e.preventDefault()
    setState('saving')
    const err = await save(username, displayName)
    if (err) { setState(err); return }
    setState('saved')
    setEditing(false)
  }

  const errorText = state === 'invalid'
    ? t('friends.handleInvalid')
    : state === 'taken'
      ? t('friends.handleTaken')
      : (state && state !== 'saving' && state !== 'saved') ? state : null

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold flex items-center gap-1.5" dir="auto">
            <AtSign className="w-4 h-4 text-app-primary-500" />
            {t('friends.myHandle')}
          </h2>
          {!editing && (
            <Button size="sm" variant="outline" onClick={startEdit}>
              {identity.username ? t('common.edit') : t('common.add')}
            </Button>
          )}
        </div>

        {/* No dir on the summary line — it starts from the page's
            reading side (right in Arabic); only the @handle inside is
            pinned LTR via <bdi>. */}
        {!editing && identity.username && (
          <p className="text-sm text-muted-foreground">
            <bdi dir="ltr">@{identity.username}</bdi>
            {identity.display_name && <bdi> · {identity.display_name}</bdi>}
            {state === 'saved' && <span className="text-emerald-500 ms-2">{t('friends.handleSaved')}</span>}
          </p>
        )}
        {!editing && !identity.username && (
          <p className="text-sm text-muted-foreground" dir="auto">{t('friends.handleHint')}</p>
        )}

        {editing && (
          <form onSubmit={submit} className="space-y-2">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              placeholder={t('friends.handlePlaceholder')}
              autoCapitalize="none"
              autoCorrect="off"
              maxLength={20}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
            />
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('friends.displayNamePlaceholder')}
              maxLength={40}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
            />
            <p className="text-[11px] text-muted-foreground" dir="auto">{t('friends.handleHint')}</p>
            {errorText && <p className="text-xs text-red-500" dir="auto">{errorText}</p>}
            <div className="flex gap-2">
              <Button type="submit" disabled={state === 'saving'} className="flex-1">
                {t('common.save')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Add a friend ───────────────────────────────────────────────────
function SearchCard({ t, friendsApi }) {
  const [handle, setHandle] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null) // null | 'none' | profile row
  const [sent, setSent] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    const q = handle.trim()
    if (!q || busy) return
    setBusy(true)
    setSent(false)
    try {
      const row = await friendsApi.search(q)
      setResult(row || 'none')
    } catch {
      setResult('none')
    }
    setBusy(false)
  }

  const add = async () => {
    if (!result || result === 'none' || busy) return
    setBusy(true)
    const ok = await friendsApi.sendRequest(result.user_id)
    setBusy(false)
    if (ok) setSent(true)
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-2">
        <h2 className="text-sm font-semibold flex items-center gap-1.5" dir="auto">
          <UserPlus className="w-4 h-4 text-app-primary-500" />
          {t('friends.searchTitle')}
        </h2>
        <form onSubmit={submit} className="flex gap-2">
          <input
            type="text"
            value={handle}
            onChange={(e) => { setHandle(e.target.value.toLowerCase()); setResult(null) }}
            placeholder={t('friends.searchPlaceholder')}
            autoCapitalize="none"
            autoCorrect="off"
            maxLength={20}
            className="h-10 flex-1 min-w-0 px-3 rounded-md border border-input bg-background text-foreground text-sm"
          />
          <Button type="submit" disabled={busy || !handle.trim()}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </form>

        {result === 'none' && (
          <p className="text-sm text-muted-foreground" dir="auto">{t('friends.noMatch')}</p>
        )}
        {result && result !== 'none' && (
          <div className="flex items-center gap-3 pt-1">
            <Avatar userId={result.user_id} profile={result} size={36} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate" dir="auto">
                {result.display_name || result.username}
              </p>
              <p className="text-xs text-muted-foreground truncate"><bdi dir="ltr">@{result.username}</bdi></p>
            </div>
            {sent ? (
              <span className="text-xs text-emerald-500 flex-shrink-0" dir="auto">{t('friends.requestSent')}</span>
            ) : (
              <Button size="sm" onClick={add} disabled={busy}>
                <UserPlus className="w-4 h-4 me-1" />
                {t('friends.add')}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Shared bits ────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className="space-y-1.5">
      <h2 className="text-sm font-semibold px-1" dir="auto">{title}</h2>
      <Card>
        <CardContent className="p-0 divide-y divide-border/60">{children}</CardContent>
      </Card>
    </div>
  )
}

function FriendRow({ row, children }) {
  return (
    <div className="p-3 flex items-center gap-3">
      <Avatar userId={row.other_user_id} profile={row} size={40} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate" dir="auto">
          {row.display_name || row.username || `${row.other_user_id.slice(0, 8)}…`}
        </p>
        {row.username && (
          <p className="text-xs text-muted-foreground truncate"><bdi dir="ltr">@{row.username}</bdi></p>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">{children}</div>
    </div>
  )
}

// One row in the suggestions section — public identity + mutual-friend
// count and a one-tap add. sendRequest's refetch removes the row (it
// becomes an outgoing pending), so only the busy state lives here.
function SuggestionRow({ suggestion, onAdd, t }) {
  const [busy, setBusy] = useState(false)
  const name = suggestion.display_name
    || (suggestion.username ? `@${suggestion.username}` : '')
  const add = async () => {
    if (busy) return
    setBusy(true)
    await onAdd()
    setBusy(false)
  }
  return (
    <div className="p-3 flex items-center gap-3">
      <Avatar userId={suggestion.user_id} profile={suggestion} size={40} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate" dir="auto">{name}</p>
        {suggestion.mutual_count > 0 ? (
          <p className="text-xs text-app-primary-500 truncate" dir="auto">
            {t('friends.mutualCount', { count: suggestion.mutual_count })}
          </p>
        ) : (
          suggestion.username && (
            <p className="text-xs text-muted-foreground truncate"><bdi dir="ltr">@{suggestion.username}</bdi></p>
          )
        )}
      </div>
      <Button size="sm" variant="outline" onClick={add} disabled={busy}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 me-1" />}
        {!busy && t('friends.add')}
      </Button>
    </div>
  )
}

// Destructive actions arm on first tap (icon → "sure?") and fire on
// the second — lighter than a dialog for row-level actions.
function IconAction({ icon: Icon, label, onConfirm, t }) {
  const [armed, setArmed] = useState(false)
  if (armed) {
    return (
      <Button
        size="sm"
        variant="destructive"
        onClick={() => { setArmed(false); onConfirm() }}
        onBlur={() => setArmed(false)}
      >
        {t('friends.confirm')}
      </Button>
    )
  }
  return (
    <button
      type="button"
      onClick={() => setArmed(true)}
      aria-label={label}
      title={label}
      className="p-2 rounded-md hover:bg-accent text-muted-foreground"
    >
      <Icon className="w-4 h-4" />
    </button>
  )
}

export default FriendsPage
